/**
 * AuthedApp — every route that needs a signed-in staff member, inside the app
 * shell.
 *
 * Two trees, one per mode: `/admin/*` is the organizational view and `/me/*`
 * the instructor's. `RequireMode` gates each on the grant that admits a viewer
 * to that workspace; the pages inside keep their own permission gates.
 * Navigating straight into the other tree switches mode — `useAppMode` reads
 * it off the address — so a deep link lands where it points rather than
 * bouncing.
 *
 * `ToastProvider` wraps the whole authed tree rather than any one page, because
 * a toast is the app's shared confirmation channel — the reservations tab is the
 * first caller, not the owner.
 */
import { Routes, Route } from 'react-router-dom';

import AdminRoutes from './AdminRoutes';
import ProtectedRoute from './auth/ProtectedRoute';
import WelcomePage from './components/auth/WelcomePage';
import ChatPanel from './components/chat/ChatPanel';
import TeacherDashboards from './components/dashboards/teacher/TeacherDashboards';
import { CurrentUserProvider } from './contexts/CurrentUserContext';
import HomeRedirect from './shared/nav/HomeRedirect';
import NebariAppShell from './shared/nav/NebariAppShell';
import RequireMode from './shared/nav/RequireMode';
import NotFoundPage from './shared/NotFoundPage';
import { ToastProvider } from './shared/toast/ToastProvider';

export default function AuthedApp() {
  return (
    <ProtectedRoute>
      <CurrentUserProvider>
        <ToastProvider>
          <NebariAppShell>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              {/* Post-invite landing: the password ticket and the Google
                  invite email both send people here. Outside both trees — the
                  viewer has no mode yet when they arrive. */}
              <Route path="/welcome" element={<WelcomePage />} />
              <Route
                path="/admin/*"
                element={
                  <RequireMode mode="admin">
                    <AdminRoutes />
                  </RequireMode>
                }
              />
              <Route
                path="/me/*"
                element={
                  <RequireMode mode="teacher">
                    <TeacherDashboards />
                  </RequireMode>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </NebariAppShell>
          <ChatPanel />
        </ToastProvider>
      </CurrentUserProvider>
    </ProtectedRoute>
  );
}
